import { ProductType } from 'src/common/interfaces/Tenant';
import { SessionViews } from 'src/common/types/WfaSessionViews';

import { TOKEN_QUERY_PARAM } from './InvitationTokenHelper';
import {
	MSDK_REPORT_ID_PARAM,
	MSDK_TEST_NAME_PARAM,
	REDIRECT_TO_FROM_404_PAGE_QUERY_PARAM
} from './QueryParamsHelper';

const productHubUrl = import.meta.env.VITE_PRODUCT_HUB_URL;

interface WebSdkInfoPathParams {
	runId: string;
	issueId: string;
}

interface WfaInfoPathParams {
	runId: string;
	issueId: string;
}

interface MfaInfoPathParams {
	runId: string;
	issueId: string;
}

interface MobileSdkInfoPathParams {
	testId: string;
	issueId: string;
}

export class RoutesHelper {
	// common
	public static getHomepagePath(): string {
		return '/';
	}

	public static getLoginPath(): string {
		return '/login';
	}

	public static getLoginSuccessPath(): string {
		return '/login-success';
	}

	public static getSignupPath(): string {
		return '/signup';
	}

	public static getInvitationActivationPage(invitationToken: string): string {
		return `/invitation-activation${
			invitationToken ? `?${TOKEN_QUERY_PARAM}=${invitationToken}` : ''
		}`;
	}

	// MFA Dashboard
	public static getMfaDashboardPath(): string {
		return '/mfa';
	}

	public static getMfaV1DashboardPath(): string {
		return '/mfa-v1';
	}

	public static getMfaSessionReportPathname(): string {
		return `${this.getMfaDashboardPath()}/report/`;
	}

	public static getMfaV1SessionReportPathname(): string {
		return `${this.getMfaV1DashboardPath()}/report/`;
	}

	public static getMfaSessionReportPath({
		sessionId,
		sessionName
	}: {
		sessionId: string;
		sessionName: string;
	}): string {
		return `${this.getMfaSessionReportPathname()}?sessionId=${sessionId}&sessionName=${sessionName}`;
	}

	public static getMfaV1SessionReportPath({
		sessionId,
		sessionName
	}: {
		sessionId: string;
		sessionName: string;
	}): string {
		return `${this.getMfaV1SessionReportPathname()}?sessionId=${sessionId}&sessionName=${sessionName}`;
	}

	public static getMfaSessionBasePath(): string {
		return `${this.getMfaDashboardPath()}/session`;
	}

	public static getMfaSessionRootPath(sessionId: string): string {
		return `${this.getMfaSessionBasePath()}/${sessionId}`;
	}

	public static getMfaSessionPath(sessionId: string, viewId: SessionViews): string {
		return `${this.getMfaSessionRootPath(sessionId)}/${viewId ?? 'overview'}`;
	}

	public static getMfaIssueInfoPath({ runId, issueId }: MfaInfoPathParams): string {
		return `${this.getMfaSessionRootPath(runId)}/issue/${issueId}`;
	}

	// Mobile SDK Dashboard
	public static getMobileSdkReportPath({
		reportId,
		testName,
		createdAt,
		userFiltersLabels
	}: {
		reportId: string;
		testName: string;
		createdAt: string;
		userFiltersLabels?: string[];
	}): string {
		return `${this.getMobileSdkTestReportPathname()}?${MSDK_REPORT_ID_PARAM}=${reportId}&${MSDK_TEST_NAME_PARAM}=${testName}&createdAt=${createdAt}&labels=${userFiltersLabels}`;
	}

	public static getMobileSdkDashboardPath(): string {
		return '/mobile-sdk';
	}

	public static getMobileSdkTestReportPathname(): string {
		return `${this.getMobileSdkDashboardPath()}/report/`;
	}

	public static getMobileSdkV1DashboardPath(): string {
		return '/mobile-sdk-v1';
	}

	public static getMobileSdkV1TestReportPathname(): string {
		return `${this.getMobileSdkV1DashboardPath()}/report/`;
	}

	public static getMobileSdkV1ReportPath({
		reportId,
		testName,
		createdAt,
		userFiltersLabels
	}: {
		reportId: string;
		testName: string;
		createdAt: string;
		userFiltersLabels?: string[];
	}): string {
		return `${this.getMobileSdkV1TestReportPathname()}?${MSDK_REPORT_ID_PARAM}=${reportId}&${MSDK_TEST_NAME_PARAM}=${testName}&createdAt=${createdAt}&labels=${userFiltersLabels}`;
	}

	public static getMobileSdkTestBasePath(): string {
		return `${this.getMobileSdkDashboardPath()}/test`;
	}

	public static getMobileSdkTestRootPath(testId: string): string {
		return `${this.getMobileSdkTestBasePath()}/${testId}`;
	}

	public static getMobileSdkTestPath(testId: string, viewId: SessionViews): string {
		return `${this.getMobileSdkTestRootPath(testId)}/${viewId ?? 'overview'}`;
	}

	public static getMobileSdkIssueInfoPath({ testId, issueId }: MobileSdkInfoPathParams): string {
		return `${this.getMobileSdkTestRootPath(testId)}/issue/${issueId}`;
	}

	// Web SDK

	public static getWebSdkDashboardPath(): string {
		return '/web-sdk';
	}

	public static getWebSdkDashboardTestPath(): string {
		return `${this.getWebSdkDashboardPath()}/test/`;
	}

	public static getWebSdkTestPath(runId: string): string {
		return `${this.getWebSdkDashboardTestPath()}${runId}`;
	}

	public static getWebSdkIssueInfoPath({ runId, issueId }: WebSdkInfoPathParams): string {
		return `${this.getWebSdkTestPath(runId)}/issue/${issueId}`;
	}

	// WFA
	public static getWfaDashboardPath(): string {
		return '/wfa';
	}

	public static getWfaSessionBasePath(): string {
		return `${this.getWfaDashboardPath()}/session`;
	}

	public static getWfaSessionRootPath(sessionId: string): string {
		return `${this.getWfaSessionBasePath()}/${sessionId}`;
	}

	public static getWfaSessionPath(sessionId: string, viewId: SessionViews): string {
		return `${this.getWfaSessionRootPath(sessionId)}/${viewId ?? 'overview'}`;
	}

	public static getWfaIssueInfoPath({ runId, issueId }: WfaInfoPathParams): string {
		return `${this.getWfaSessionRootPath(runId)}/issue/${issueId}`;
	}

	// New Dashboard Paths

	public static getAccessibilityDashboardPath(): string {
		return '/dashboard';
	}

	public static getDashboardIssuesPath(): string {
		return `${this.getAccessibilityDashboardPath()}/issues`;
	}

	public static getBusinessUnitDashboardPath(unitId?: string): string {
		const basePath = `${this.getAccessibilityDashboardPath()}/business-unit`;
		return unitId ? `${basePath}/${unitId}` : basePath;
	}

	// Mobile Dashboard
	public static getMobileAccessibilityDashboardPath(): string {
		return '/mobile-dashboard';
	}

	public static getMobileDashboardIssuesPath(): string {
		return `${this.getMobileAccessibilityDashboardPath()}/issues`;
	}

	public static getMobileBusinessUnitDashboardPath(unitId?: string): string {
		const basePath = `${this.getMobileAccessibilityDashboardPath()}/business-unit`;
		return unitId ? `${basePath}/${unitId}` : basePath;
	}

	public static getBusinessUnitPath(basePath: string, unitId?: string): string {
		const path = `${basePath}/business-unit`;
		return unitId ? `${path}/${unitId}` : path;
	}

	public static getIssuesPath(basePath: string): string {
		return `${basePath}/issues`;
	}

	public static getLabelManagementPath(): string {
		return '/labels-management';
	}

	// External URLs
	public static getProductHubUrl(): string {
		return productHubUrl;
	}

	public static getMfaProductHubUrl(): string {
		return `${productHubUrl}/mobile-flow-analyzer`;
	}

	public static getUsersInvitationsHubPath(): string {
		return `${productHubUrl}/invitations`;
	}

	public static getUnauthorized403Page = (): string => {
		return '/unauthorized';
	};

	public static getNotFound404Page = (redirectTo?: string): string => {
		return `/not-found${
			redirectTo ? `?${REDIRECT_TO_FROM_404_PAGE_QUERY_PARAM}=${redirectTo}` : ''
		}`;
	};

	public static getJiraAuthenticationPath = (): string => {
		return '/jira-authentication';
	};

	public static getAzureAuthenticationPath(): string {
		return '/azure-authentication';
	}

	public static getConnectPath(): string {
		return '/connect';
	}
}

export const getProductTypeFromPath = (): ProductType => {
	const { pathname } = window.location;
	if (pathname.includes(RoutesHelper.getMfaDashboardPath())) {
		return ProductType.MOBILE_FLOW_ANALYZER;
	}
	if (pathname.includes(RoutesHelper.getWfaDashboardPath())) {
		return ProductType.WEB_FLOW_ANALYZER;
	}
	if (pathname.includes(RoutesHelper.getMobileSdkDashboardPath())) {
		return ProductType.MOBILE_SDK;
	}
	if (pathname.includes(RoutesHelper.getMobileSdkV1DashboardPath())) {
		return ProductType.MOBILE_SDK;
	}
	if (pathname.includes(RoutesHelper.getWebSdkDashboardPath())) {
		return ProductType.WEB_SDK;
	}

	return ProductType.HOME;
};
